import { Component, Vue, Prop } from 'vue-property-decorator';
import { ReplacementState, ReplacementInscriptionData, Sorter, ReplacementDocument, ReplacementListState } from '@/Domain/Entities';
import { StatusReplacement, ReplacementReasons } from '@/Domain/enum';
import * as notification from '../../../node_modules/saviafront/lib/js/compiled/notification';
import { ToasterService } from '@/Services/ToasterService';
import i18n from '../../lang';
import _ from 'lodash';
import { mapGetters, mapState } from 'vuex';
import DocumentService from '@/Application/Services/DocumentService';
import ReplacementStatesService from '@/Services/ReplacementStatesService';
import ReplacementService from '@/Services/ReplacementService';

@Component({
    computed: {
        statusOrderReverse() {
            return _.orderBy(this.$data.replacementData.statusList, 'date').reverse();
        },
        replacementListStateOptions() {
            return this.$data.replacementListStates.filter((state: ReplacementListState) => state.isVisible)
                .map((state: ReplacementListState) => ({
                    title: state.name[this.$i18n.locale],
                    name: state.name[this.$i18n.locale],
                    id: state
                })
            );
        },
        selectedReplacementListState(): ReplacementListState | null {
            if (this.$data.replacementStatus === null ||
                this.$data.replacementStatus === undefined ||
                this.$data.replacementStatus.replacementListState === null ||
                this.$data.replacementStatus.replacementListState === undefined) {
                return null;
            }

            return this.$data.replacementListStates.find(
                s => s.id === this.$data.replacementStatus.replacementListState.id
            ) || null;
        },
        ...mapState('dateFormatStore', { dateFormat: 'dateFormat'}),
        ...mapState('languagesStore', { currentLanguage: 'current' })
    }
})

export default class ReplacementStatus extends Vue {
    @Prop() replacementListCandidateId!: string;
    @Prop() replacementListModified!: string;
    dateFormat!: string;
    currentLanguage!: string;

    StatusReplacement = StatusReplacement;
    ReplacementReasons = ReplacementReasons;
    loading: boolean = false;
    openState: boolean = false;
    replacementStatus = null as any;
    replacementData = null as any;
    showDeleteModal: boolean = false;
    sorter = new Sorter();
    fistStatusRegister: boolean = false;
    submitted: boolean = false;
    replacementListStates: ReplacementListState[] = [];

    $refs!: {
        replacementStatusForm: HTMLFormElement
    };

    setReplacementStatus() {
        this.replacementStatus.reason = null;
        this.replacementStatus.status = this.replacementStatus.replacementListState.value;
    }

    editingState(replacementStatusEditing) {
        this.openState = true;
        this.replacementStatus = _.cloneDeep(replacementStatusEditing);
        this.replacementStatus.isEditing = true;
    }

    createState() {
        this.openState = true;
        this.replacementStatus = new ReplacementState({});
    }

    confirmWithModal(replacementStatusToRemove: ReplacementState) {
        this.replacementStatus = replacementStatusToRemove;
        this.showDeleteModal = true;
    }

    removeState() {
        this.showDeleteModal = false;
        this.loading = true;
        ReplacementStatesService.removeReplacementState(this.$props.replacementListCandidateId, this.replacementStatus.infoToServer())
            .then(() => {
                this.initList();
                this.loading = false;
                this.openState = false;
                this.fistStatusRegister = false;
                ToasterService.showSuccess(i18n.t('lang.toaster.saveChanged') as any, i18n.t('lang.toaster.goodJob') as any);
                this.$emit('updateReplacementListIsModify');
            })
            .catch(() => this.loading = false);
    }

    cancel() {
        this.openState = false;
        this.replacementStatus = null;
        this.submitted = false;
        this.fistStatusRegister = false;
    }

    getInscriptionReplacementAction() {
        if (this.replacementStatus.isEditing) {
            return ReplacementStatesService.updateReplacementState(this.$props.replacementListCandidateId, this.replacementStatus.infoToServer());
        }
        return ReplacementStatesService.addReplacementState(this.$props.replacementListCandidateId, this.replacementStatus.infoToServer());
    }

    save() {
        this.submitted = true;
        if (this.$refs.replacementStatusForm.querySelectorAll(':invalid').length) {
            return;
        }
        this.loading = true;
        const action = this.getInscriptionReplacementAction();
        action.then(response => {
            if (!this.replacementStatus.isEditing) {
                this.replacementStatus.id = response.id;
            }
            this.manageDocuments(this.replacementStatus.documents)
                .then(() => {
                    this.openState = false;
                    this.loading = false;
                    this.submitted = false;
                    this.fistStatusRegister = false;
                    ToasterService.showSuccess(i18n.t('lang.toaster.saveChanged') as any, i18n.t('lang.toaster.goodJob') as any);
                    this.initList();
                    this.$emit('updateReplacementListIsModify');
                })
                .catch(() => this.loading = false);
        })
        .catch(() => this.loading = false);
    }

    manageDocuments(documents) {
        return new Promise<void>((resolve, reject) => {
            if (documents.length && this.replacementStatus.replacementListState.requiresDocument) {
                const requests: any[] = [];
                documents.forEach(file => {
                    if (!file.id) {
                        requests.push(ReplacementStatesService.addFileReplacementState(this.replacementStatus, file.toServer(), this.$props.replacementListCandidateId));
                    } else if (file.delete) {
                        requests.push(ReplacementStatesService.removeFileFromReplacementState(this.replacementStatus, file.toServer(), this.$props.replacementListCandidateId));
                    }
                });
                Promise.all(requests)
                .then(() => {
                    resolve();
                })
                .catch(() => {
                    reject();
                });
            } else {
                resolve();
            }
        }).catch(error => console.log(error));
    }

    addFile(file) {
        if (this.replacementStatus.documents.some((doc: ReplacementDocument) => file.name === doc.fileName)) {
            return;
        }
        file.fileName = file.name;
        this.replacementStatus.addDocument(file);
    }

    removeFile(document: ReplacementDocument) {
        if (!document.id) {
            const index = this.replacementStatus.documents.indexOf(document);
            if (index >= 0) {
                this.replacementStatus.documents.splice(index, 1);
            }
            return;
        }
        document.delete = true;
    }

    getInscriptionReplacementState() {
        ReplacementStatesService.getReplacementStates(this.$props.replacementListCandidateId)
            .then(response => {
                this.replacementData = new ReplacementInscriptionData(response);
                this.loading = false;
                this.$nextTick(() => {
                    notification.initialize();
                });
            })
            .catch(() => this.loading = false);
    }

    downloadFile(file) {
        DocumentService.download(file);
    }

    initList() {
        this.loading = true;
        this.getInscriptionReplacementState();
    }

    async loadData() {
        this.loading = true;

        try {
            const [replacementDataResponse, replacementListStatesResponse] =
                await Promise.all([
                    ReplacementStatesService.getReplacementStates(this.replacementListCandidateId),
                    ReplacementService.getReplacementListStates()
                ]);

            this.replacementData = new ReplacementInscriptionData(replacementDataResponse);
            this.replacementListStates = replacementListStatesResponse.map(state => new ReplacementListState(state));

            this.$nextTick(() => {
                notification.initialize();
            });

        } catch (error) {
            console.error(error);
        } finally {
            this.loading = false;
        }
    }

    mounted() {
        this.loadData();
    }
}
